<template>
  <div class="w50 pd-rt-7 chart-box">
    <div class="created-resolved-chart">
      <div class="chart-top">
        <div class="chart-top-lt">
          <h2>
            {{ constants.CREATED_VS_RESOLVED_ISSUES_REPORT_TEXT }}
          </h2>
          <p>
            {{ constants.CREATED_RESOLVED_CHART_TEXT }}
          </p>
        </div>
        <div class="chart-top-rt">
          <div class="calendar-drop-down-box"  :class="{ 'show-calendar-dropdown': isCalendarOpen }">
            <div class="calendar-drop-down-icon" @click="isCalendarOpen = !isCalendarOpen">
              <img src="@/assets/lems-v1/images/calendar.svg" alt="" />
            </div>
            <div class="calendar-drop-down-content">
              <div class="calendar-drop-down-content-inner">
                <div class="choose-calendar">
                  <DatePicker v-model="selectedDate" inline @update:modelValue="validateDate" :max-date="new Date()" />
                </div>
                <div class="custom-filter">
                  <button id="allCalendarBtn" @click="filterData('all')">All</button>
                  <button id="lastWeekCalendarBtn" @click="filterData('last_week')">Last Week</button>
                  <button id="lastMonthCalendarBtn" @click="filterData('last_month')">Last Month</button>
                  <button id="lastYearCalendarBtn" @click="filterData('last_year')">Last Year</button>
                  <!-- <button id="testDataBtn" @click="loadSampleData" style="background-color: #ff9800; color: white; margin-left: 10px;">Test Data</button> -->
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="chart-btm">
        <div class="chart-show-container">
          <canvas ref="chartCanvas" width="400" height="200"></canvas>
        </div>
      </div>
      <div class="chart-table-view">
        <div class="created-resolved-table">
          <div class="c-r-table-hd c-r-table-row">
            <ul>
              <li>
                {{ constants.PERIOD_TEXT }}
              </li>
              <li>
                {{ constants.CREATED_TEXT }}
              </li>
              <li>
                {{ constants.RESOLVED_TEXT }}
              </li>
            </ul>
          </div>
          <div class="c-r-table-body">
            <div
              v-for="(item, index) in tableData"
              :key="index"
              class="c-r-table-row"
              @click="openIssuesPopup(item)"
              style="cursor: pointer;"
            >
              <ul>
                <li :data-title="constants.PERIOD_TEXT || 'Period'">
                  {{ item.period }}
                </li>
                <li :data-title="constants.CREATED_TEXT || 'Created'">{{ item.created }}</li>
                <li :data-title="constants.RESOLVED_TEXT || 'Resolved'">{{ item.resolved }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Chart, registerables } from "chart.js";
import constants from "@/utils/en-US-Employee.json";
import axios from "axios";
import { API_BASE_URL } from "@/config/api.config";
import { DatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import moment from "moment";

// Register Chart.js components
Chart.register(...registerables);

interface MonthlyPoint {
  month: string;
  created: number;
  resolved: number;
}

export default defineComponent({
  name: "Chart4",
  data() {
    return {
      constants,
      chart: null as any,
      username: "",
      projectKey: "",
      monthlyData: [] as MonthlyPoint[],
      isLoading: false,
      chartData: {
        labels: [] as string[],
        datasets: [
          {
            label: 'Created',
            data: [] as number[],
            borderColor: '#e91e63',
            backgroundColor: 'rgba(233, 30, 99, 0.1)',
            borderWidth: 3,
            fill: false,
            tension: 0.4,
            pointBackgroundColor: '#e91e63',
            pointBorderColor: '#e91e63',
            pointRadius: 4,
            pointHoverRadius: 6
          },
          {
            label: 'Resolved',
            data: [] as number[],
            borderColor: '#4caf50',
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            borderWidth: 3,
            fill: false,
            tension: 0.4,
            pointBackgroundColor: '#4caf50',
            pointBorderColor: '#4caf50',
            pointRadius: 4,
            pointHoverRadius: 6
          }
        ]
      },
      tableData: [] as { period: string; created: number; resolved: number }[],
      selectedDate: null as Date | null,
      isCalendarOpen: false,
    };
  },
  components: {
    DatePicker,
  },
  mounted() {
    this.initializeData();
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy();
    }
  },
  methods: {
    async initializeData() {
      // Get username from localStorage
      this.username = localStorage.getItem("jira_user_username") || "";
      
      // Get project key from route params
      this.projectKey = this.$route.params.projectKey as string;
      
      console.log("Initializing chart4 with:", { username: this.username, projectKey: this.projectKey });
      
      if (this.username && this.projectKey) {
        await this.fetchMonthlyIssuesData();
      } else {
        // Show empty data if no project key
        console.log("No username or project key, showing empty data");
        this.monthlyData = [];
        this.updateChartData();
        this.updateTableData();
      }
      
      // Initialize chart after data is loaded
      this.$nextTick(() => {
        this.initChart();
      });
    },
    async fetchMonthlyIssuesData() {
      this.isLoading = true;
      try {
        console.log(`Fetching data for user: ${this.username}, project: ${this.projectKey}`);
        const response = await axios.get(
          `${API_BASE_URL}/jira/monthly-issues/${this.username}/${this.projectKey}/?user_filter=created`
        );
        
        console.log("API Response:", response.data);
        this.monthlyData = response.data.monthly_data || [];
        console.log("Monthly data:", this.monthlyData);
        
        // If no data found, show a message
        if (this.monthlyData.length === 0) {
          console.log("No issues found for this user/project combination. This could mean:");
          console.log("1. The user hasn't created any issues in this project");
          console.log("2. The project key might be incorrect");
          console.log("3. The user might not have access to this project");
          console.log("4. There might be no issues in the specified time range");
        }
        
        this.updateChartData();
        this.updateTableData();
        
        // Update chart if it exists
        if (this.chart) {
          this.chart.data.labels = this.chartData.labels;
          this.chart.data.datasets[0].data = this.chartData.datasets[0].data;
          this.chart.data.datasets[1].data = this.chartData.datasets[1].data;
          this.chart.update();
        }
      } catch (error) {
        console.error("Error fetching monthly issues data:", error);
        // Show empty data instead of sample data
        this.monthlyData = [];
        this.updateChartData();
        this.updateTableData();
        
        // Update chart with empty data
        if (this.chart) {
          this.chart.data.labels = this.chartData.labels;
          this.chart.data.datasets[0].data = this.chartData.datasets[0].data;
          this.chart.data.datasets[1].data = this.chartData.datasets[1].data;
          this.chart.update();
        }
      } finally {
        this.isLoading = false;
      }
    },
    updateChartData() {
      const labels = this.monthlyData.map(item => item.month);
      const createdData = this.monthlyData.map(item => item.created);
      const resolvedData = this.monthlyData.map(item => item.resolved);

      console.log("Updating chart data:", { labels, createdData, resolvedData });

      this.chartData.labels = labels;
      this.chartData.datasets[0].data = createdData;
      this.chartData.datasets[1].data = resolvedData;
    },
    updateTableData() {
      this.tableData = this.monthlyData.map(item => ({
        period: item.month,
        created: item.created,
        resolved: item.resolved
      }));
    },
    initChart() {
      // Destroy existing chart if it exists
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      
      const ctx = this.$refs.chartCanvas as HTMLCanvasElement;
      
      console.log("Initializing chart with data:", this.chartData);
      
      this.chart = new Chart(ctx, {
        type: 'line',
        data: this.chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 20,
                font: {
                  size: 12,
                  weight: 'bold'
                }
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: '#fff',
              bodyColor: '#fff',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              borderWidth: 1
            }
          },
          scales: {
            x: {
              display: true,
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.1)',
              },
              ticks: {
                color: '#666',
                font: {
                  size: 11
                }
              }
            },
            y: {
              display: true,
              beginAtZero: true,
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.1)',
              },
              ticks: {
                color: '#666',
                font: {
                  size: 11
                }
              }
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          },
          elements: {
            point: {
              hoverRadius: 8
            }
          }
        }
      });
    },
    async filterData(period: string) {
      // Fetch data based on the selected period
      if (this.username && this.projectKey) {
        try {
          const params = new URLSearchParams({
            filter: period,
            user_filter: 'created',
            custom_date: this.selectedDate ? moment(this.selectedDate).format("YYYY-MM-DD") : ''
          });
          const response = await axios.get(
            `${API_BASE_URL}/jira/monthly-issues/${this.username}/${this.projectKey}/?${params.toString()}`
          );
          
          this.monthlyData = response.data.monthly_data || [];
          this.updateChartData();
          this.updateTableData();
          
          if (this.chart) {
            // Update existing chart data instead of recreating
            this.chart.data.labels = this.chartData.labels;
            this.chart.data.datasets[0].data = this.chartData.datasets[0].data;
            this.chart.data.datasets[1].data = this.chartData.datasets[1].data;
            this.chart.update('active');
          }
        } catch (error) {
          console.error("Error fetching filtered data:", error);
          // Show empty data on error
          this.monthlyData = [];
          this.updateChartData();
          this.updateTableData();
      
      if (this.chart) {
            this.chart.data.labels = this.chartData.labels;
            this.chart.data.datasets[0].data = this.chartData.datasets[0].data;
            this.chart.data.datasets[1].data = this.chartData.datasets[1].data;
        this.chart.update('active');
          }
        }
      }
    },
    openIssuesPopup(item: { period: string; created: number; resolved: number }) {
      // Emit event to parent component to pass data to popup
      this.$emit('open-issues-popup', {
        month: item.period,
        projectKey: this.projectKey,
        created: item.created,
        resolved: item.resolved
      });
    },
    loadSampleData() {
      console.log("Loading sample data for testing...");
      this.monthlyData = [
        { month: 'Jan', created: 5, resolved: 3 },
        { month: 'Feb', created: 8, resolved: 6 },
        { month: 'Mar', created: 12, resolved: 9 },
        { month: 'Apr', created: 7, resolved: 8 },
        { month: 'May', created: 15, resolved: 12 },
        { month: 'Jun', created: 10, resolved: 11 }
      ];
      this.updateChartData();
      this.updateTableData();
      
      if (this.chart) {
        this.chart.data.labels = this.chartData.labels;
        this.chart.data.datasets[0].data = this.chartData.datasets[0].data;
        this.chart.data.datasets[1].data = this.chartData.datasets[1].data;
        this.chart.update();
      }
    },
    validateDate(date: Date) {
      if (date) {
        this.isCalendarOpen = false;
        this.selectedDate = date;
        this.filterData('custom_date');
      }
      else {
        this.isCalendarOpen = false;
        this.selectedDate = null;
        this.filterData('all'); 
      }
    },
    closeCalendar() {
      this.isCalendarOpen = false;
    },
  },
  watch: {
    // Watch for changes in the route parameter
    '$route.params.projectKey': {
      handler(newKey, oldKey) {
        if (newKey !== oldKey && newKey) {
          this.projectKey = newKey as string;
          this.initializeData();
        }
      },
      immediate: false
    }
  }
});
</script>

<style scoped>
.chart-show-container {
  position: relative;
  height: 300px;
  width: 100%;
  padding: 20px;
}

.chart-show-container canvas {
  max-width: 100%;
  height: 100% !important;
}

/* Add some styling for better visual appeal */
.custom-filter button {
  cursor: pointer;
  transition: all 0.3s ease;
}

.custom-filter button:hover {
  opacity: 0.8;
}
</style>